// src/modules/requerimiento/components/adicion/EquipoInstaladoDialog.jsx
import React, { useEffect, useRef, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  IconButton, Stack, TextField, Typography,
} from "@mui/material";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import MemoryIcon from "@mui/icons-material/Memory";
import ModemImageManager from "../ModemImageManager";
import { updateModemDatos } from "../../services/reqSolicitudes";

const AZUL_EQUIPOS = "#0284C7";

const formDe = (row) => ({
  id_ref: row?.id_ref || row?.alias || "",
  transportista: row?.transportista || "",
  grupo: row?.grupo || "",
  sociedad: row?.sociedad || "",
});

/**
 * Detalle de un equipo ya instalado en un vehículo (Trabajo de campo y Revisión en Sistemas):
 * placa, equipo, datos del vehículo editables, foto de la instalación y registro.
 * Guarda con el mismo PUT de datos del módem y la imagen con ModemImageManager.
 */
export default function EquipoInstaladoDialog({
  row,            // equipo a mostrar; null = cerrado
  cod,
  onClose,
  onSaved,        // tras guardar o cambiar la imagen (recargar la lista)
  onToast,
  onVerEquipo,    // (numSim) => abre el Centro de equipos
  toAbsUrl,
  editarAlAbrir = false,
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(formDe(null));
  const [imagePending, setImagePending] = useState({ hasFile: false, error: "" });
  const imageManagerRef = useRef(null);

  const numSim = row?.num_sim;
  // Al abrir otro equipo se parte de sus datos guardados.
  useEffect(() => {
    if (!numSim) return;
    setForm(formDe(row));
    setEditing(editarAlAbrir);
    setImagePending({ hasFile: false, error: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numSim]);

  const handleClose = () => {
    if (saving) return;
    imageManagerRef.current?.clearPendingImage?.();
    setEditing(false);
    onClose?.();
  };

  const handleCancelEdit = () => {
    setForm(formDe(row));
    imageManagerRef.current?.clearPendingImage?.();
    setImagePending({ hasFile: false, error: "" });
    setEditing(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSave = async () => {
    if (!cod || !numSim) return;
    if (imagePending.error) {
      onToast?.({ sev: "warning", msg: imagePending.error });
      return;
    }
    const body = {
      id_ref: form.id_ref || "",
      grupo: form.grupo || "",
      sociedad: form.sociedad || "",
      transportista: form.transportista || "",
    };
    try {
      setSaving(true);
      await updateModemDatos(cod, numSim, body);
      const savedImage = await imageManagerRef.current?.savePendingImage?.({ notify: false });
      setEditing(false);
      setImagePending({ hasFile: false, error: "" });
      await onSaved?.({ ...row, ...body });
      onToast?.({ sev: "success", msg: savedImage ? "Datos e imagen actualizados" : "Datos del módem actualizados" });
    } catch (e) {
      onToast?.({
        sev: "error",
        msg: e?.response?.data?.message || e?.response?.data?.error || e?.message || "No se pudo actualizar los datos",
      });
    } finally {
      setSaving(false);
    }
  };

  const chips = getChips(row);

  return (
    <Dialog
      open={!!row}
      onClose={saving ? undefined : handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: "12px" } }}
    >
      {row && (
        <>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2.5, pt: 2, pb: 1.5 }}>
            <Box
              sx={{
                width: 44, height: 44, borderRadius: "8px", color: "#fff", display: "grid", placeItems: "center", flexShrink: 0,
                background: "linear-gradient(135deg, #16A34A, #166534)",
              }}
            >
              <DirectionsCarIcon />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.2 }}>
                {editing ? "Editando datos del vehículo" : "Equipo instalado en"}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }} noWrap>
                {row.placa || "Sin placa"}
              </Typography>
            </Box>
            {row.estado && (
              <Chip size="small" icon={<CheckCircleIcon />} label={row.estado} color="success" variant="outlined" sx={{ fontWeight: 600 }} />
            )}
            <IconButton aria-label="Cerrar" onClick={handleClose} disabled={saving} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          <DialogContent dividers sx={{ px: 2.5 }}>
            <Stack spacing={2.5}>
              <Seccion titulo="Equipo">
                <Box
                  onClick={() => numSim && onVerEquipo?.(numSim)}
                  sx={{
                    display: "flex", alignItems: "center", gap: 1.5, p: 1.5, borderRadius: "10px",
                    border: "1px solid", borderColor: "divider", cursor: numSim ? "pointer" : "default",
                    "&:hover": numSim ? { borderColor: "primary.main", bgcolor: "action.hover" } : undefined,
                  }}
                >
                  <Box sx={{ width: 40, height: 40, borderRadius: "50%", bgcolor: "rgba(2,132,199,0.12)", color: AZUL_EQUIPOS, display: "grid", placeItems: "center", flexShrink: 0 }}>
                    <MemoryIcon fontSize="small" />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: AZUL_EQUIPOS }} noWrap>
                      {row.emai || numSim || "—"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {[numSim && numSim !== row.emai ? `SIM ${numSim}` : null, row.modelo].filter(Boolean).join(" · ") || "—"}
                    </Typography>
                  </Box>
                  {numSim && (
                    <Typography variant="caption" color="primary" sx={{ fontWeight: 600, flexShrink: 0 }}>
                      Ver equipo
                    </Typography>
                  )}
                </Box>
                <Grilla>
                  <Dato label={chips.length > 1 ? "Chips" : "Chip"} value={chips.length ? chips.map(getChipNumber).join(", ") : null} />
                  <Dato label="Origen / IP" value={row.ip} />
                </Grilla>
              </Seccion>

              <Seccion titulo="Vehículo">
                {editing ? (
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
                    <TextField size="small" label="Alias" name="id_ref" value={form.id_ref} onChange={handleChange} fullWidth autoFocus />
                    <TextField size="small" label="Transportista" name="transportista" value={form.transportista} onChange={handleChange} fullWidth />
                    <TextField size="small" label="Grupo" name="grupo" value={form.grupo} onChange={handleChange} fullWidth />
                    <TextField size="small" label="Sociedad" name="sociedad" value={form.sociedad} onChange={handleChange} fullWidth />
                  </Box>
                ) : (
                  <Grilla>
                    <Dato label="Alias" value={row.id_ref} />
                    <Dato label="Transportista" value={row.transportista} />
                    <Dato label="Grupo" value={row.grupo} />
                    <Dato label="Sociedad" value={row.sociedad} />
                  </Grilla>
                )}
              </Seccion>

              <Seccion titulo="Foto de la instalación">
                <ModemImageManager
                  ref={imageManagerRef}
                  cod={cod}
                  numSim={numSim}
                  initialUrl={row.imagen_url || ""}
                  toAbsUrl={toAbsUrl}
                  editMode={editing}
                  hideSaveButton={editing}
                  readOnly={!editing}
                  onPendingChange={setImagePending}
                  onChanged={async () => { await onSaved?.(row); }}
                  onToast={onToast}
                />
              </Seccion>

              <Seccion titulo="Registro">
                <Grilla>
                  <Dato label="Asignado" value={row.asignado} />
                  <Dato label="Propiedad" value={row.propiedad} />
                  <Dato label="Ciudad" value={row.ciudad} />
                  <Dato label="Usuario" value={row.usu} />
                </Grilla>
                {row.observacion && <Observacion texto={row.observacion} />}
              </Seccion>
            </Stack>
          </DialogContent>

          <DialogActions sx={{ px: 2.5, py: 1.5 }}>
            {editing ? (
              <>
                <Button onClick={handleCancelEdit} disabled={saving} sx={{ textTransform: "none" }}>Cancelar</Button>
                <Button
                  onClick={handleSave}
                  variant="contained"
                  disabled={saving || !!imagePending.error}
                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                  sx={{ textTransform: "none", fontWeight: 700 }}
                >
                  {saving ? "Guardando…" : "Guardar cambios"}
                </Button>
              </>
            ) : (
              <>
                <Button onClick={handleClose} sx={{ textTransform: "none" }}>Cerrar</Button>
                <Button
                  onClick={() => setEditing(true)}
                  variant="contained"
                  startIcon={<EditIcon />}
                  sx={{ textTransform: "none", fontWeight: 700 }}
                >
                  Editar datos
                </Button>
              </>
            )}
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}

export function getChips(row) {
  return Array.isArray(row?.chips) ? row.chips : [];
}

export function getChipNumber(chip) {
  return chip?.numero || chip?.num_sim || chip?.chip || "-";
}

/** Título de sección en mayúsculas pequeñas y su contenido debajo. */
export function Seccion({ titulo, children }) {
  return (
    <Box>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ display: "block", fontWeight: 700, letterSpacing: 0.6, lineHeight: 1.6, mb: 1 }}
      >
        {titulo}
      </Typography>
      <Stack spacing={1.5}>{children}</Stack>
    </Box>
  );
}

export function Grilla({ children, columnas = 2 }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: `repeat(${columnas}, 1fr)` },
        columnGap: 2,
        rowGap: 1.5,
      }}
    >
      {children}
    </Box>
  );
}

/** Dato con su etiqueta; vacío se muestra como "—" atenuado. */
export function Dato({ label, value }) {
  const vacio = value == null || String(value).trim() === "";
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontWeight: vacio ? 400 : 600, color: vacio ? "text.disabled" : "text.primary", wordBreak: "break-word" }}
      >
        {vacio ? "—" : value}
      </Typography>
    </Box>
  );
}

export function Observacion({ texto }) {
  return (
    <Box sx={{ p: 1.5, borderRadius: "10px", bgcolor: "action.hover" }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>Observación</Typography>
      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{texto}</Typography>
    </Box>
  );
}
